import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Upload, X } from "lucide-react";

export default function ProductsTab({ products, API, authHeaders, onReload }) {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showNeedsPhoto, setShowNeedsPhoto] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const needsPhotoCount = useMemo(
    () => products.filter(p => !(p.images?.[0]||'').startsWith('data:')).length,
    [products]
  );
  const visibleProducts = useMemo(
    () => products.filter(p => !showNeedsPhoto || !(p.images?.[0]||'').startsWith('data:')),
    [products, showNeedsPhoto]
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/products/${deleteTarget.product_id}`, { headers: authHeaders() });
      toast.success(`Deleted: ${deleteTarget.name}`);
      setDeleteTarget(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={()=>{setEditing(null); setShowForm(true);}} className="btn-primary inline-flex items-center gap-2" data-testid="add-product-btn"><Plus size={16}/> NEW PRODUCT</button>
        <button onClick={()=>setShowNeedsPhoto(v=>!v)} className={`btn-outline inline-flex items-center gap-2 ${showNeedsPhoto?'bg-[var(--ink)] text-[var(--paper)]':''}`} data-testid="filter-needs-photo">
          {showNeedsPhoto ? "SHOWING: NEEDS PHOTO" : `FILTER: NEEDS PHOTO (${needsPhotoCount})`}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-product-grid">
        {visibleProducts.map(p => {
          const needsPhoto = !(p.images?.[0]||'').startsWith('data:');
          return (
            <div key={p.product_id} className="card-industrial p-3 relative" data-testid={`admin-prod-${p.product_id}`}>
              {needsPhoto && <div className="absolute top-1 right-1 bg-[var(--accent)] text-white font-mono text-[9px] font-bold px-2 py-1 border-2 border-[var(--ink)] z-10" data-testid={`needs-photo-${p.product_id}`}>PHOTO NEEDED</div>}
              <img src={p.images?.[0]} className="w-full h-32 object-cover border border-[var(--ink)] mb-2" alt=""/>
              <div className="font-display text-sm mb-1">{p.name}</div>
              <div className="font-mono text-xs mb-2">${p.price} · Stock: {p.stock}</div>
              <div className="flex gap-2">
                <button onClick={()=>{setEditing(p); setShowForm(true);}} className="flex-1 btn-outline py-1" data-testid={`edit-${p.product_id}`}><Edit size={14}/></button>
                <button onClick={()=>setDeleteTarget(p)} className="flex-1 btn-outline py-1" data-testid={`del-${p.product_id}`}><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
      </div>
      {showForm && <ProductForm product={editing} API={API} authHeaders={authHeaders} onClose={()=>{setShowForm(false); onReload();}}/>}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={()=>!deleting && setDeleteTarget(null)} data-testid="delete-confirm-modal">
          <div className="bg-[var(--paper)] border-2 border-[var(--ink)] max-w-md w-full" onClick={e=>e.stopPropagation()}>
            <div className="p-5 border-b-2 border-[var(--ink)] flex items-center gap-3">
              <div className="bg-[var(--accent)] text-white w-10 h-10 flex items-center justify-center border-2 border-[var(--ink)]"><Trash2 size={18}/></div>
              <h2 className="font-display text-xl">DELETE PRODUCT?</h2>
            </div>
            <div className="p-5">
              <p className="font-mono text-sm mb-2">This will permanently remove:</p>
              <div className="bg-[var(--muted)] border-2 border-[var(--ink)] p-3 mb-4 flex gap-3 items-center">
                {deleteTarget.images?.[0] && <img src={deleteTarget.images[0]} className="w-16 h-16 object-cover border border-[var(--ink)]" alt=""/>}
                <div>
                  <div className="font-display text-base">{deleteTarget.name}</div>
                  <div className="font-mono text-xs text-[var(--ink-2)]">{deleteTarget.sku} · ${deleteTarget.price}</div>
                </div>
              </div>
              <p className="font-mono text-xs text-[var(--ink-2)]">This action cannot be undone.</p>
            </div>
            <div className="p-5 border-t-2 border-[var(--ink)] flex gap-3 justify-end">
              <button onClick={()=>setDeleteTarget(null)} disabled={deleting} className="btn-outline" data-testid="delete-cancel-btn">NO, KEEP IT</button>
              <button onClick={confirmDelete} disabled={deleting} className="btn-primary bg-[var(--accent)] border-[var(--accent)]" data-testid="delete-confirm-btn">{deleting?"DELETING...":"YES, DELETE"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductForm({ product, API, authHeaders, onClose }) {
  const [f, setF] = useState(product || { name:"", description:"", price:0, stock:0, category:"General", sku:"", images:[] });

  const handleImage = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF({...f, images:[...f.images, reader.result]});
    reader.readAsDataURL(file);
  };
  const removeImg = (i) => setF({...f, images: f.images.filter((_,idx)=>idx!==i)});

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, price: parseFloat(f.price), stock: parseInt(f.stock), original_price: f.original_price ? parseFloat(f.original_price) : null };
    try {
      if (product) await axios.put(`${API}/products/${product.product_id}`, body, { headers: authHeaders() });
      else await axios.post(`${API}/products`, body, { headers: authHeaders() });
      toast.success("Saved"); onClose();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--paper)] border-2 border-[var(--ink)] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="product-form">
        <div className="flex justify-between p-5 border-b-2 border-[var(--ink)]">
          <h2 className="font-display text-2xl">{product?"EDIT":"NEW"} PRODUCT</h2>
          <button onClick={onClose}><X/></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="font-mono text-xs block mb-1 tracking-wider">NAME</label>
            <input required placeholder="e.g. 2001 Ford Taurus" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="input-field" data-testid="prod-name"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-1 tracking-wider">DESCRIPTION</label>
            <textarea placeholder="Condition, features, notes for buyers..." value={f.description} onChange={e=>setF({...f,description:e.target.value})} rows="3" className="input-field" data-testid="prod-desc"/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-xs block mb-1 tracking-wider">PRICE ($)</label>
              <input required type="number" step="0.01" placeholder="0.00" value={f.price} onChange={e=>setF({...f,price:e.target.value})} className="input-field" data-testid="prod-price"/>
            </div>
            <div>
              <label className="font-mono text-xs block mb-1 tracking-wider">QTY</label>
              <input required type="number" placeholder="0" value={f.stock} onChange={e=>setF({...f,stock:e.target.value})} className="input-field" data-testid="prod-stock"/>
            </div>
            <div>
              <label className="font-mono text-xs block mb-1 tracking-wider">SKU</label>
              <input placeholder="LCW-1000" value={f.sku} onChange={e=>setF({...f,sku:e.target.value})} className="input-field" data-testid="prod-sku"/>
            </div>
          </div>
          <div>
            <label className="font-mono text-xs block mb-1 tracking-wider">ORIGINAL PRICE (optional — for SALE badge)</label>
            <input type="number" step="0.01" placeholder="0.00" value={f.original_price || ""} onChange={e=>setF({...f,original_price:e.target.value})} className="input-field" data-testid="prod-original-price"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-1 tracking-wider">CATEGORY</label>
            <input placeholder="e.g. Autos & Parts" value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="input-field" data-testid="prod-cat"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-2 tracking-wider">PRODUCT IMAGES</label>
            <label className="btn-outline inline-flex items-center gap-2 cursor-pointer"><Upload size={16}/> UPLOAD IMAGE<input type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="img-upload"/></label>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {f.images.map((img,i) => <div key={`${img.slice(0,32)}-${i}`} className="relative"><img src={img} className="w-full h-20 object-cover border-2 border-[var(--ink)]" alt=""/><button type="button" onClick={()=>removeImg(i)} className="absolute top-0 right-0 bg-[var(--accent)] text-white p-1"><X size={12}/></button></div>)}
            </div>
          </div>
          <button className="btn-primary w-full" data-testid="save-prod">SAVE PRODUCT</button>
        </form>
      </div>
    </div>
  );
}
